import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
// import { useFormContext } from 'react-hook-form'
import { Collapse } from '@mui/material'
import { TextField, Autocomplete, SelectField } from '../../Base/Form/index.js'
import SanitizeHtml from '../../Base/SanitizeHtml/SanitizeHtml.js'
import * as lib from '../../../../js/lib/lib.js'
import { useSelector } from 'react-redux'

import { z } from 'zod'

export const driverDetailsSchema = (milesNBonusAccount, mbData, dictionary) => {
  const {
    firstName: firstNameMB,
    lastName: lastNameMB,
    fFN,
  } = milesNBonusAccount || {}

  const { redeemEnabled } = mbData || {}
  const checkSameAsMB = redeemEnabled && !!fFN

  return z.object({
    firstName: z
      .string({
        required_error: dictionary.firstNameRequiredText,
      })
      .min(1, dictionary.firstNameRequiredText)
      .min(2, dictionary.firstNameLengthText)
      .max(30, dictionary.firstNameLengthText)
      .refine(
        (value) => {
          if (!checkSameAsMB) {
            return true
          }
          return lib.maskStringLowerCase(value) === firstNameMB?.toLowerCase()
        },
        {
          message: dictionary.firstNameMBMatchText,
        }
      ),
    lastName: z
      .string({
        required_error: dictionary.lastNameRequiredText,
      })
      .min(1, dictionary.lastNameRequiredText)
      .min(2, dictionary.lastNameLengthText)
      .max(30, dictionary.lastNameLengthText)
      .refine(
        (value) => {
          if (!checkSameAsMB) {
            return true
          }
          return lib.maskStringLowerCase(value) === lastNameMB?.toLowerCase()
        },
        {
          message: dictionary.lastNameMBMatchText,
        }
      ),
    emailGroup: z
      .object({
        email: z
          .string({
            required_error: dictionary.emailRequiredText,
          })
          .min(1, dictionary.emailRequiredText)
          .min(6, dictionary.emailLengthText)
          .max(50, dictionary.emailLengthText)
          .email(dictionary.emailInvalidText || 'Invalid email format'),
        verifyEmail: z
          .string({
            required_error: dictionary.emailVerifyRequiredText,
          })
          .min(1, dictionary.emailVerifyRequiredText)
          .min(6, dictionary.emailVerifyLengthText)
          .max(50, dictionary.emailVerifyLengthText)
          .email(dictionary.emailVerifyInvalidText || 'Invalid email format'),
      })
      .refine((data) => data.email === data.verifyEmail, {
        message: dictionary.emailsDoNotMatchText,
        path: ['verifyEmail'],
      }),
    mobilePhone: z
      .string({
        required_error: dictionary.mobilePhoneRequiredText,
      })
      .min(1, dictionary.mobilePhoneRequiredText)
      .regex(/^(\+1)?\d{10,14}$/, dictionary.invalidMobileNumberText),
    mobileDialingCode: z
      .object(
        { text: z.string(), value: z.string() },
        {
          required_error: dictionary.mobileCountryCodeRequiredText,
          invalid_type_error: dictionary.mobileCountryCodeRequiredText,
        }
      )
      .refine((val) => !!val?.value && !!val?.text, {
        message: dictionary.mobileCountryCodeRequiredText,
      }),
    landPhone: z
      .string({
        required_error: dictionary.landPhoneRequiredText,
      })
      .min(1, dictionary.landPhoneRequiredText)
      .regex(/^(\+1)?\d{10,14}$/, dictionary.invalidLandNumberText),
    landDialingCode: z
      .object(
        { text: z.string(), value: z.string() },
        {
          required_error: dictionary.landCountryCodeRequiredText,
          invalid_type_error: dictionary.landCountryCodeRequiredText,
        }
      )
      .refine((val) => !!val?.value && !!val?.text, {
        message: dictionary.landCountryCodeRequiredText,
      }),
  })
}

const DriverDetails = forwardRef(
  (
    {
      control,
      errors,
      setValue,
      trigger,
      // setIsSubscribed,
      // setIsMarketing,
      onEmailBlur,
    },
    ref
  ) => {
    const { dictionary, countryCodeOptions, flightList, driversList } =
      window.bookingData

    const { hasFlightNumber } = useSelector((state) => state.booking)
    const [toggleDriverDetails, setToggleDriverDetails] = useState(true)

    useImperativeHandle(ref, () => ({
      expand: () => {
        setToggleDriverDetails(true)
      },
    }))

    const handleCollapseClick = () => {
      if (lib.scrDevices()) {
        setToggleDriverDetails(!toggleDriverDetails)
      }
    }

    const onSelectDriver = (driver) => {
      const { name, surname } = driversList.find(
        (d) => d.value === driver.value
      )

      setValue('firstName', name)
      setValue('lastName', surname)
      trigger('firstName')
      trigger('lastName')
    }

    useEffect(() => {
      setTimeout(() => {
        if (flightList.length > 0) {
          setValue('driversList', driversList[0].value)
          onSelectDriver(driversList[0])
        }
      })
    }, [])

    useEffect(() => {
      // Preselect dialing codes based on location from localStorage
      const location = localStorage.getItem('location')
      if (location && countryCodeOptions) {
        const matchingOption = countryCodeOptions.find(
          (option) => option.value === location
        )
        if (matchingOption) {
          setValue('mobileDialingCode', matchingOption)
          setValue('landDialingCode', matchingOption)
        }
      }
    }, [])

    useEffect(() => {
      const handleResize = () => {
        if (!lib.scrDevices()) {
          setToggleDriverDetails(true)
        }
      }

      window.addEventListener('resize', handleResize)
      handleResize()

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    return (
      <>
        <div className="s-booking-form__form-group">
          <div
            className="row"
            role="button"
            tabIndex={0}
            onClick={handleCollapseClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleCollapseClick()
              }
            }}
            aria-expanded={toggleDriverDetails}
            aria-label="Toggle driver details section">
            <div className="col-lg-6 mb-24 d-flex align-items-center justify-content-between">
              <span className="s-booking-form__form-group__title">
                {dictionary.driverDetailsLabel}
              </span>
              <div className="d-lg-none ">
                <i
                  className={`icon-arrow-down arrow ${
                    toggleDriverDetails ? 'open' : ''
                  }`}
                />
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end align-items-end mb-24">
              <span className="s-booking-form__form-group__required">
                {dictionary.driverDetailsRequiredLabel}
              </span>
            </div>
          </div>

          <Collapse in={toggleDriverDetails}>
            {hasFlightNumber && (
              <div className="mb-24">
                <SelectField
                  name="driversList"
                  label={'Select driver'}
                  control={control}
                  options={driversList}
                  onChange={(e) => onSelectDriver(e.target)}
                />
              </div>
            )}

            <div className="row">
              <div className="col-lg-6 mb-24">
                <TextField
                  name="firstName"
                  label={dictionary.driverDetailsFirstNameLabel}
                  control={control}
                  type="text"
                  error={errors.firstName}
                  disabled={hasFlightNumber}
                />
              </div>
              <div className="col-lg-6 mb-24">
                <TextField
                  name="lastName"
                  label={dictionary.driverDetailsLastNameLabel}
                  control={control}
                  type="text"
                  error={errors.lastName}
                  disabled={hasFlightNumber}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12 mb-24">
                <TextField
                  name="emailGroup.email"
                  label={dictionary.driverDetailsEmailLabel}
                  control={control}
                  type="text"
                  error={errors.emailGroup?.email}
                  onBlur={onEmailBlur}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12 mb-24">
                <TextField
                  name={'emailGroup.verifyEmail'}
                  label={dictionary.driverDetailsVerifyEmailLabel}
                  control={control}
                  type="text"
                  error={errors.emailGroup?.verifyEmail}
                  onPaste={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="row">
              {countryCodeOptions && (
                <div className="col-lg-6 mb-24">
                  <Autocomplete
                    name={'mobileDialingCode'}
                    label={dictionary.driverDetailsMobileDialingCodeLabel}
                    control={control}
                    options={countryCodeOptions}
                    getOptionLabel={(option) => option.text}
                    error={errors.mobileDialingCode}
                    onInputChange={() => {}}
                  />
                </div>
              )}
              <div className="col-lg-6 mb-32">
                <TextField
                  name={'mobilePhone'}
                  label={dictionary.driverDetailsMobilePhoneLabel}
                  control={control}
                  type="text"
                  error={errors.mobilePhone}
                />
              </div>
            </div>
            <div className="row">
              {countryCodeOptions && (
                <div className="col-lg-6 mb-24">
                  <Autocomplete
                    name={'landDialingCode'}
                    label={dictionary.driverDetailsLandDialingCodeLabel}
                    control={control}
                    options={countryCodeOptions}
                    getOptionLabel={(option) => option.text}
                    error={errors.landDialingCode}
                    onInputChange={() => {}}
                  />
                </div>
              )}
              <div className="col-lg-6 mb-32">
                <TextField
                  name={'landPhone'}
                  label={dictionary.driverDetailsLandPhoneLabel}
                  control={control}
                  type="text"
                  error={errors.landPhone}
                />
              </div>
            </div>
            {/* <div className="form-check">
            <input
              id="marketing"
              type="checkbox"
              name="marketing"
              className="form-check-input"
              onChange={(e) => setIsMarketing(e.target.checked)}
            />
            <SanitizeHtml
              tag="label"
              html={dictionary.driverDetailsMarketingText}
              htmlFor="marketing"
              className="form-check-label"
            />
          </div> */}
          </Collapse>
        </div>
      </>
    )
  }
)

DriverDetails.displayName = 'DriverDetails'

export default DriverDetails
